// Achados 1/2/5 (28/09/2026) — hook compartilhado da foto dos colegas.
//
// Antes deste hook, `CollaboratorsList` era o ÚNICO lugar que carregava
// TODOS os colaboradores internos (com `foto_url`), num laço paginado, em
// estado local. `MessageBubble`, `ChatRoomAvatar` e `MemberList` renderizavam
// `UserAvatar` SEM `avatarUrl` — não por regra (a decisão de 25/09/2026 já
// libera a foto entre colaboradores internos, `_foto_liberada_entre` em
// `app/api/v1/endpoints/users.py`), mas por FALTA DO DADO: o WebSocket manda
// a MESMA mensagem pra sala inteira, e a URL assinada depende de QUEM vê —
// não dá pra embutir no payload da mensagem/sala.
//
// A saída é a mesma lista que a `CollaboratorsList` já carregava, promovida a
// hook compartilhado (react-query, uma chave só): quem abre `/mensagens`
// paga UMA requisição paginada (não duas — a lista e o chat compartilham o
// cache), e qualquer avatar de colaborador interno (bolha, cabeçalho de DM,
// linha de membro) lê a foto dali.
//
// 29/09/2026 — a lista agora é "as pessoas da comunicação": a requisição pede
// `para_comunicacao=true` e a regra (interno, ativo, não excluído, sem marca de
// conta de sistema) mora no servidor. Os três diálogos de membros (sala, grupo
// existente, grupo novo) tinham cada um a própria cópia do laço paginado, sem
// esse filtro — ofereciam desligados e contas de sistema. Agora leem daqui.
// Consequência para o avatar: a foto de quem saiu, ou de conta de sistema, cai
// nas iniciais (decisão de 27/09 para desligado; conta de sistema idem), porque
// essas pessoas não vêm mais na lista.
import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/services/api";
import { urlDaFotoPequena } from "@/lib/fotoPequena";

export interface ColegaInterno {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  perfil?: string | null;
  setor?: string | null;
  // Decisão de 25/09/2026: a foto sai para colaborador interno
  // (`_foto_liberada_entre`). URL assinada, ou ausente.
  foto_url?: string | null;
  // Passo 27 — a miniatura (256 px) da mesma foto; o que as telas devem pedir.
  foto_miniatura_url?: string | null;
}

// Mesmo tamanho de página que a `CollaboratorsList` sempre usou.
const PAGE_SIZE = 100;

function getUserId(u: { id?: string; user_id?: string }): string {
  return u.id || u.user_id || "";
}

/**
 * Carrega TODOS os colaboradores internos, paginando até esgotar — "todos" é
 * todos, sem teto artificial (mesmo laço que a `CollaboratorsList` usava
 * antes deste hook, só que compartilhado). `page > 100` é rede de segurança
 * (10 mil pessoas), não um limite de produto.
 */
async function carregarTodosOsColegas(): Promise<ColegaInterno[]> {
  const all: ColegaInterno[] = [];
  let page = 1;

  while (true) {
    const r = await api.get("/users", {
      params: {
        page,
        page_size: PAGE_SIZE,
        user_types: "internal",
        // 29/09/2026 — só quem pode aparecer na comunicação (interno, ativo,
        // não excluído, sem marca de conta de sistema). A regra mora no servidor.
        para_comunicacao: true,
      },
      paramsSerializer: (p) => {
        const u = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((vv) => u.append(k, String(vv)));
          else if (v != null) u.append(k, String(v));
        });
        return u.toString();
      },
    });
    const batch = Array.isArray(r.data) ? r.data : r.data?.users || r.data?.items || [];
    const tot = r.data?.total_count || r.data?.total || batch.length;
    all.push(...batch.map((u: ColegaInterno) => ({ ...u, id: getUserId(u) })));
    if (batch.length < PAGE_SIZE || all.length >= tot) break;
    page += 1;
    if (page > 100) break; // safety: max 10k users
  }
  return all;
}

/** Chave única do react-query — todo consumidor deste módulo compartilha o cache. */
const QUERY_KEY = ["colegas-internos"] as const;

/**
 * Todas as pessoas da comunicação (colaboradores internos ativos, sem conta de
 * sistema), com foto (quando existir).
 * `staleTime` de 10min: a lista muda pouco (admissão/desligamento), e não
 * vale reabrir o laço paginado a cada troca de aba.
 */
export function useColegasInternos(): UseQueryResult<ColegaInterno[]> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: carregarTodosOsColegas,
    staleTime: 10 * 60 * 1000,
  });
}

/** Nome para exibir: "Nome Sobrenome", ou o e-mail quando não há nome. */
export function nomeDoColega(u: Pick<ColegaInterno, "first_name" | "last_name" | "email">): string {
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
}

/**
 * Ordem alfabética por nome, em pt-BR (acento não desloca a letra). O hook
 * devolve a lista crua — cada consumidor decide a própria ordem —, mas todas as
 * telas que mostram pessoas ordenam do mesmo jeito, então a regra mora aqui em
 * vez de repetida em cada diálogo. Não altera a lista recebida.
 */
export function ordenarColegasPorNome<
  T extends Pick<ColegaInterno, "first_name" | "last_name" | "email">,
>(colegas: readonly T[]): T[] {
  return [...colegas].sort((a, b) => nomeDoColega(a).localeCompare(nomeDoColega(b), "pt-BR"));
}

/**
 * As mesmas pessoas do `useColegasInternos` (mesma chave, mesmo cache), já em
 * ordem alfabética e com os estados que os diálogos de membros precisam:
 * carregando e erro. Memoizado — não reordena a cada render, só quando a lista
 * muda.
 */
export function useColegasOrdenados(): {
  colegas: ColegaInterno[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useColegasInternos();
  const colegas = useMemo(() => ordenarColegasPorNome(data ?? []), [data]);
  return { colegas, isLoading, isError };
}

/**
 * O `Map` id → foto (a miniatura, desde o passo 27) de TODOS os colegas,
 * memoizado: só é refeito quando a lista muda, então o mesmo objeto volta a cada
 * render. Quem mostra muitas pessoas de uma vez (a lista de "quem reagiu", com
 * até ~150 linhas) lê o `Map` aqui, uma vez, em vez de abrir um
 * `useFotoDoColega` — um observador de `useQuery` — por
 * linha. `null` = sem foto; id fora do `Map` = fora da lista (parceiro, ou quem
 * saiu). Enquanto carrega, um `Map` vazio.
 */
export function useFotosDosColegas(): Map<string, string | null> {
  const { data } = useColegasInternos();

  return useMemo(() => {
    const mapa = new Map<string, string | null>();
    for (const colega of data ?? []) {
      mapa.set(colega.id, urlDaFotoPequena(colega));
    }
    return mapa;
  }, [data]);
}

/**
 * A foto de UM colega, buscada na lista compartilhada — O(1) por chamada via
 * o `Map` memoizado de `useFotosDosColegas` (não refaz o `Map` a cada render,
 * só quando a lista muda).
 * `null` = sem foto, carregando, ou usuário fora da lista (parceiro, ou
 * alguém que saiu — a decisão de 25/09/2026 não estende a eles).
 */
export function useFotoDoColega(userId: string | null | undefined): string | null {
  const fotoPorId = useFotosDosColegas();

  if (!userId) return null;
  return fotoPorId.get(userId) ?? null;
}
